'use client';

import { useMemo, useState } from 'react';

interface EvidenceSource {
  link: string;
  title?: string;
  statement?: string;
  excerpt?: string;
  conditions?: string;
}

interface SponsorEvidenceSectionProps {
  id: string;
  label: string;
  score?: number;
  count: number;
  primarySource?: EvidenceSource;
  sources: EvidenceSource[];
}

export function SponsorEvidenceSection({
  id,
  label,
  score,
  count,
  primarySource,
  sources,
}: SponsorEvidenceSectionProps) {
  const nonFeaturedSources = useMemo(() => {
    if (!primarySource) return sources;
    return sources.filter(source => source.link !== primarySource.link);
  }, [primarySource, sources]);

  const [showAll, setShowAll] = useState(false);
  const visibleSources = useMemo(() => {
    if (showAll) return nonFeaturedSources;
    return nonFeaturedSources.slice(0, 5);
  }, [nonFeaturedSources, showAll]);

  const hasMore = nonFeaturedSources.length > 5;

  return (
    <section
      id={id}
      className="rounded-3xl border border-default-200 bg-white p-8 shadow-sm"
      data-testid={`sponsor-evidence-section-${id}`}
    >
      <div className="flex flex-col gap-4 border-b border-default-200 pb-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-primary">{label}</h3>
          <p className="mt-1 text-sm sm:text-base text-default-500">
            Score contribution: <span className="font-medium text-default-700">{score ?? '—'}</span>{' '}
            — {count} supporting sources.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1 text-sm font-medium text-primary">
            Score {score ?? '—'}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-default-200 px-4 py-1 text-sm font-medium text-default-600">
            {count} references
          </span>
        </div>
      </div>

      {primarySource && (
        <div className="mt-6 rounded-2xl bg-primary/5 p-5">
          <span className="text-xs sm:text-sm uppercase tracking-wide text-primary">
            Featured Reference
          </span>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <a
              href={primarySource.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base sm:text-lg font-semibold text-primary underline-offset-2 hover:underline"
            >
              {primarySource.title || primarySource.link}
            </a>
            <span className="text-sm text-default-500">Opens in new window</span>
          </div>
          {primarySource.excerpt && (
            <p className="mt-4 text-sm text-default-600 leading-relaxed">{primarySource.excerpt}</p>
          )}
          {primarySource.statement && (
            <p className="mt-4 text-sm italic text-default-600">
              &ldquo;{primarySource.statement}&rdquo;
            </p>
          )}
          {primarySource.conditions && (
            <p className="mt-4 text-sm text-default-600">
              <span className="font-medium text-default-700">Conditions:</span>{' '}
              {primarySource.conditions}
            </p>
          )}
        </div>
      )}

      <div className="mt-8 space-y-5">
        {visibleSources.map(source => (
          <div
            key={source.link}
            className="rounded-2xl border border-default-200 p-5 transition hover:border-primary/30 hover:shadow-md"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <a
                href={source.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base sm:text-lg font-semibold text-primary underline-offset-2 hover:underline"
              >
                {source.title || source.link}
              </a>
              <span className="text-xs uppercase tracking-wide text-default-400">Source</span>
            </div>
            {source.excerpt && (
              <p className="mt-3 text-sm text-default-600 leading-relaxed">{source.excerpt}</p>
            )}
            {source.statement && (
              <p className="mt-3 text-sm italic text-default-600">
                &ldquo;{source.statement}&rdquo;
              </p>
            )}
            {source.conditions && (
              <p className="mt-3 text-sm text-default-600">
                <span className="font-medium text-default-700">Conditions:</span>{' '}
                {source.conditions}
              </p>
            )}
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll(prev => !prev)}
            className="px-6 py-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 text-sm font-medium transition"
          >
            {showAll ? 'Show less' : `Show all ${count} references`}
          </button>
        </div>
      )}
    </section>
  );
}
